import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, ShieldCheck, MonitorSmartphone, CreditCard, Fingerprint, Settings, LogOut,
  Package,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { billingApi, hardwareOrdersApi } from '../utils/api';
import CustomerSwitcher from './CustomerSwitcher';

// Shared chrome for every authenticated portal page.
//
// Before this, ProtectedRoute only gated auth and each page hand-rolled its own
// header — so only the Dashboard actually had one, and /reports had neither a
// header nor a back button (a genuine dead end for clients: its only control
// was the staff impersonation banner). One layout means the nav is always
// present, so no page can strand a client again.
//
// Nav lives next to the logo; only the email + Sign Out sit on the right.
const NAV_ITEMS = [
  { key: 'home',    label: 'Home',    icon: Home,             path: '/' },
  { key: 'hipaa',   label: 'HIPAA',   icon: ShieldCheck,      path: '/compliance',
    match: ['/compliance', '/reports', '/report'] },
  // RMM has no client-facing page yet — shown disabled so the nav layout is
  // final and clients can see it's coming.
  { key: 'rmm',     label: 'RMM',     icon: MonitorSmartphone, path: null, disabled: true,
    title: 'RMM — coming soon' },
  { key: 'billing', label: 'Billing', icon: CreditCard,       path: '/billing', billingOnly: true },
  // Orders only appears when the customer actually has hardware orders.
  { key: 'orders',  label: 'Orders',  icon: Package,          path: '/orders', ordersOnly: true },
  { key: 'sentry',  label: 'Sentry',  icon: Fingerprint,      path: '/badge' },
];

// Account settings sits on the right with Sign Out rather than in the primary
// nav — it's account-level, not one of the portal's destinations.
const ACCOUNT_PATHS = ['/account', '/change-password'];

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [hasOrders, setHasOrders] = useState(false);

  useEffect(() => {
    // Best-effort: the Billing tab simply stays hidden if this fails or the
    // customer isn't billing-enabled.
    billingApi.isEnabled().then(setBillingEnabled).catch(() => setBillingEnabled(false));
    // Same for Orders — the tab only shows when hardware orders exist.
    hardwareOrdersApi.list().then(o => setHasOrders(o.length > 0)).catch(() => setHasOrders(false));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (item) => {
    const paths = item.match || (item.path ? [item.path] : []);
    return paths.some(p => (p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)));
  };

  return (
    <div className="portal-shell">
      <header className="portal-header">
        <div className="portal-header-left">
          <img src="/logo.svg" alt="Macula Networks" className="portal-logo" />
          <nav className="portal-nav">
            {NAV_ITEMS.filter(i => (!i.billingOnly || billingEnabled) && (!i.ordersOnly || hasOrders)).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`portal-nav-btn ${isActive(item) ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                  onClick={() => !item.disabled && item.path && navigate(item.path)}
                  disabled={item.disabled}
                  title={item.title || item.label}
                >
                  <Icon size={17} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="portal-header-right">
          <CustomerSwitcher />
          <span className="user-email">{user?.email}</span>
          <button
            type="button"
            className={`portal-account-btn ${ACCOUNT_PATHS.some(p => location.pathname.startsWith(p)) ? 'active' : ''}`}
            onClick={() => navigate('/account')}
            title="Account Settings"
            aria-label="Account Settings"
          >
            <Settings size={17} />
          </button>
          <button className="logout-button" onClick={handleLogout}>
            <LogOut size={17} />
            Sign Out
          </button>
        </div>
      </header>

      <main className="portal-main">{children}</main>
    </div>
  );
}
